/**
 * Public status page — no auth required.
 *
 * Polls GET /status from the backend every 10 seconds and renders each
 * dependency check (modal, vectorize, postgres, KV, AI) as a colored row.
 *
 * Mounted at the top level (not inside _auth) so it's reachable when WorkOS
 * is down or the user isn't signed in. Useful as an external uptime check
 * target and as a "is the site up?" landing page.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

interface CheckResult {
  ok?: boolean;
  status?: number;
  error?: string;
  reason?: string;
  vectorCount?: number | string;
}

interface StatusResponse {
  status: string;
  version?: string;
  timestamp: string;
  duration_ms?: number;
  region?: string;
  checks: Record<string, CheckResult>;
}

const POLL_INTERVAL_MS = 10_000;

function StatusPage() {
  const [data, setData] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const apiBase = import.meta.env.VITE_API_URL?.trim().replace(/\/$/, "") || "";
        const res = await fetch(`${apiBase}/status`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as StatusResponse;
        if (!cancelled) {
          setData(json);
          setError(null);
          setLastFetchedAt(new Date());
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLastFetchedAt(new Date());
        }
      }
    }

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Compute the worst-case status for the page header
  const allOk = data && Object.values(data.checks).every((c) => c.ok === true);
  const overallStatus = error ? "down" : allOk ? "ok" : data ? "degraded" : "loading";

  const statusLabel = {
    ok: "All systems operational",
    degraded: "Partial degradation",
    down: "Cannot reach backend",
    loading: "Checking status\u2026",
  }[overallStatus];

  const statusColor = {
    ok: "#22c55e",
    degraded: "#f59e0b",
    down: "#ef4444",
    loading: "#9ca3af",
  }[overallStatus];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--background, #0a0a0a)",
        color: "var(--foreground, #fafafa)",
        fontFamily: "system-ui, -apple-system, sans-serif",
        padding: "2rem 1rem",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <h1 style={{ fontSize: "2rem", marginBottom: "0.25rem" }}>Writ Status</h1>
        <p style={{ color: "#9ca3af", marginTop: 0, marginBottom: "2rem", fontSize: "0.875rem" }}>
          Live health check of the Writ control plane and its dependencies.
        </p>

        {/* Top-level summary */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "1rem 1.25rem",
            border: "1px solid #262626",
            borderRadius: 8,
            background: "#0f0f0f",
            marginBottom: "1.5rem",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: statusColor,
              boxShadow: `0 0 12px ${statusColor}`,
            }}
          />
          <span style={{ fontSize: "1.125rem", fontWeight: 500 }}>{statusLabel}</span>
        </div>

        {/* Per-check rows */}
        {data ? (
          <div
            style={{
              border: "1px solid #262626",
              borderRadius: 8,
              background: "#0f0f0f",
              overflow: "hidden",
            }}
          >
            {Object.entries(data.checks).map(([name, check], i, arr) => {
              const ok = check.ok === true;
              const dotColor = ok ? "#22c55e" : "#ef4444";
              const detail =
                check.error ??
                check.reason ??
                (check.status ? `HTTP ${check.status}` : null) ??
                (check.vectorCount !== undefined ? `${check.vectorCount} vectors` : null);
              return (
                <div
                  key={name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.875rem 1.25rem",
                    borderBottom: i < arr.length - 1 ? "1px solid #1f1f1f" : "none",
                    fontSize: "0.9375rem",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: dotColor,
                    }}
                  />
                  <span style={{ flex: 1, textTransform: "capitalize" }}>{name}</span>
                  <span style={{ color: "#9ca3af", fontSize: "0.8125rem" }}>
                    {ok ? "operational" : "down"}
                  </span>
                  {detail && (
                    <span style={{ color: "#71717a", fontSize: "0.75rem", marginLeft: "0.5rem" }}>
                      ({detail})
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : error ? (
          <div
            style={{
              padding: "1rem 1.25rem",
              border: "1px solid #ef4444",
              borderRadius: 8,
              background: "#1a0a0a",
              color: "#fca5a5",
            }}
          >
            <strong>Cannot reach status endpoint.</strong>
            <div style={{ fontSize: "0.8125rem", marginTop: "0.25rem" }}>{error}</div>
          </div>
        ) : (
          <div style={{ color: "#9ca3af", padding: "1rem 0" }}>Loading\u2026</div>
        )}

        {/* Footer */}
        <div
          style={{
            marginTop: "1.5rem",
            color: "#71717a",
            fontSize: "0.75rem",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>
            {lastFetchedAt
              ? `Last checked ${lastFetchedAt.toLocaleTimeString()}`
              : "Polling every 10s"}
          </span>
          {data?.region && <span>Edge: {data.region}</span>}
          {data?.duration_ms !== undefined && <span>{data.duration_ms}ms</span>}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/status")({
  component: StatusPage,
});
